<template>
	<v-card
		width="100%"
		class="inline-card sgnl-p-8"
		:class="{ 'inline-card--loading': loadingData, 'marked-for-disconnect': markedForDisconnect }"
		variant="outlined"
	>
		<template v-if="!loadingData">
			<div class="inline-card__row">
				<div class="inline-card__source sgnl-text-truncate">
					<div v-if="dataSourceData">
						<v-tooltip
							open-delay="50"
							:disabled="!$vuetify.display.mobile"
						>
							<template v-slot:activator="{ props }">
								<div
									v-bind="props"
									class="sgnl-flex sgnl-align-center sgnl-paragraph-s-medium sgnl-text-color-default sgnl-gap-4"
								>
									<FolderShared16
										v-if="dataSourceType === 'SignaloidCloudStorage'"
										class="sgnl-flex-shrink-0 sgnl-icon-color-secondary"
									/>
									<span
										v-else-if="dataSourceType === 'Bucket'"
										class="data-source-icon data-source-icon--bucket"
										role="img"
										aria-label="S3 Bucket"
									/>
									<span
										v-else-if="dataSourceType === 'Drive'"
										class="data-source-icon data-source-icon--drive"
										role="img"
										aria-label="Data Drive"
									/>
									<span
										v-else-if="dataSourceType === 'Gateway'"
										class="data-source-icon data-source-icon--gateway"
										role="img"
										aria-label="Gateway"
									/>
									<v-icon v-else>{{ dataSourceIcon(dataSourceType) }}</v-icon>
									<span class="sgnl-text-truncate">{{ dataSourceData.Name }}</span>
								</div>
							</template>
							<span>{{ dataSourceData.Name }}</span>
						</v-tooltip>
					</div>
					<div v-else>
						<v-alert
							class="sgnl-flex sgnl-justify-center sgnl-m-0 sgnl-p-0 sgnl-px-4 sgnl-caption-regular"
							width="100%"
							variant="outlined"
							type="warning"
							density="compact"
							:icon="dataSourceIcon(dataSourceType)"
							><strong>Data source not found!</strong>
						</v-alert>
					</div>
				</div>

				<template v-if="mountLocation">
					<ArrowRight16 class="sgnl-flex-shrink-0 sgnl-icon-color-secondary" />
					<div class="inline-card__mount sgnl-text-truncate">
						<v-tooltip
							open-delay="50"
							:disabled="!$vuetify.display.mobile"
						>
							<template v-slot:activator="{ props }">
								<div
									v-bind="props"
									class="sgnl-paragraph-s-regular sgnl-text-truncate sgnl-text-color-secondary"
								>
									./{{ mountLocation }}/
								</div>
							</template>
							<span>./{{ mountLocation }}/</span>
						</v-tooltip>
					</div>
				</template>
				<template v-else>
					<div class="inline-card__mount">
						<v-alert
							class="sgnl-flex sgnl-justify-center sgnl-m-0 sgnl-p-0 sgnl-px-4 sgnl-caption-regular"
							width="100%"
							variant="outlined"
							type="warning"
							density="compact"
							:icon="'mdi-folder-download-outline'"
							><strong>Mount Location not found!</strong>
						</v-alert>
					</div>
				</template>

				<div
					v-if="!hideDisconnectButton"
					class="inline-card__action sgnl-flex-shrink-0"
				>
					<TooltipButton
						btnClass="sgnl-p-0 transition-slow-in-slow-out"
						size="24"
						:icon="true"
						variant="text"
						:color="markedForDisconnect ? '' : 'error'"
						@click="toggleSourceDisconnectMark"
						:tooltipText="markedForDisconnect ? disconnectTooltip.marked : disconnectTooltip.unmarked"
					>
						<template #btnContent>
							<Renew16
								v-if="markedForDisconnect"
								class="sgnl-icon-color-brand"
							/>
							<TrashCan16 v-else />
						</template>
					</TooltipButton>
				</div>
			</div>
		</template>

		<div
			v-else
			class="inline-card__row"
		>
			<div class="inline-card__source sgnl-flex sgnl-align-center sgnl-gap-4">
				<SkeletonBlock
					width="var(--sgnl-icon-16)"
					height="var(--sgnl-icon-16)"
				/>
				<SkeletonBlock
					width="70%"
					class="sgnl-paragraph-s-medium"
				/>
			</div>
			<template v-if="mountLocation">
				<ArrowRight16 class="sgnl-flex-shrink-0 sgnl-icon-color-secondary" />
				<div class="inline-card__mount">
					<SkeletonBlock
						width="60%"
						class="sgnl-paragraph-s-regular"
					/>
				</div>
			</template>
			<div
				v-if="!hideDisconnectButton"
				class="inline-card__action sgnl-flex-shrink-0"
			>
				<SkeletonBlock
					width="var(--sgnl-unit-24)"
					height="var(--sgnl-unit-24)"
					radius="var(--sgnl-border-radius-6)"
				/>
			</div>
		</div>
	</v-card>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { sdk } from "@/js/sdk";
import * as dsUtil from "@/components/DataSources/utilities";
import TooltipButton from "@/components/Common/TooltipButton.vue";
import ArrowRight16 from "@carbon/icons-vue/es/arrow--right/16";

import TrashCan16 from "@carbon/icons-vue/es/trash-can/16";
import Renew16 from "@carbon/icons-vue/es/renew/16";
import FolderShared16 from "@carbon/icons-vue/es/folder--shared/16";
import SkeletonBlock from "@/components/Common/SkeletonBlock.vue";
import { AWSS3Bucket, DataDrive, DataSourceType } from "@/types/api/dataSources";
import { monitoringCaptureError } from "@/plugins/monitoring";

type ComponentData = {
	dataSourceData: undefined | DataSourceData;
	loadingData: boolean;
	markedForDisconnect: boolean;
};

type DataSourceData =
	| AWSS3Bucket
	| DataDrive
	| {
			Name: "Signaloid Cloud Storage";
	  };
export default defineComponent({
	name: "DataSourceInlineCard",
	components: {
		TooltipButton,
		ArrowRight16,
		TrashCan16,
		Renew16,
		FolderShared16,
		SkeletonBlock,
	},
	emits: ["reset-data-source", "reset-source-disconnect-mark", "mark-source-for-disconnect"],
	props: {
		dataSourceID: { type: String, required: true },
		dataSourceType: { type: String as PropType<DataSourceType>, required: true },
		mountLocation: { type: String, default: "" },
		disconnectTooltip: {
			type: Object,
			default: () => ({
				marked: "Keep Mounted",
				unmarked: "Mark for Unmounting",
			}),
		},
		hideDisconnectButton: {
			type: Boolean,
			default: false,
		},
	},
	data: (): ComponentData => ({
		dataSourceData: undefined,
		loadingData: false,
		markedForDisconnect: false,
	}),
	methods: {
		async getSourceInfo() {
			this.loadingData = true;
			switch (this.dataSourceType) {
				case "Gateway":
					try {
						const resp = await sdk.things.getOne(this.dataSourceID);
						this.dataSourceData = resp as unknown as DataSourceData;
					} catch (error) {
						monitoringCaptureError(error, "Fetch data source info");
					} finally {
						this.loadingData = false;
					}
					break;
				case "Bucket":
					try {
						const resp = await sdk.buckets.getOne(this.dataSourceID);
						this.dataSourceData = resp as unknown as DataSourceData;
					} catch (error) {
						monitoringCaptureError(error, "Fetch data source info");
					} finally {
						this.loadingData = false;
					}
					break;
				case "Drive":
					try {
						const resp = await sdk.drives.getOne(this.dataSourceID);
						this.dataSourceData = resp as unknown as DataSourceData;
					} catch (error) {
						monitoringCaptureError(error, "Fetch data source info");
					} finally {
						this.loadingData = false;
					}
					break;
				case "SignaloidCloudStorage":
					try {
						this.dataSourceData = {
							Name: "Signaloid Cloud Storage",
						};
					} catch (error) {
						monitoringCaptureError(error, "Fetch data source info");
					} finally {
						this.loadingData = false;
					}
					break;
				default:
					this.$emit("reset-data-source");
					throw "Error: Unexpected source type";
			}
		},
		toggleSourceDisconnectMark() {
			if (this.markedForDisconnect) {
				this.markedForDisconnect = false;
				this.$emit("reset-source-disconnect-mark", this.dataSourceID);
			} else {
				this.markedForDisconnect = true;
				this.$emit("mark-source-for-disconnect", this.dataSourceID);
			}
		},
		dataSourceIcon(dataSourceType: DataSourceType) {
			return dsUtil.dataSourceIcon(dataSourceType);
		},
	},
	mounted() {
		this.getSourceInfo();
	},
});
</script>

<style lang="scss" scoped>
.inline-card--loading {
	min-height: 42px;
	display: grid;
	align-content: center;
}

.inline-card__row {
	display: flex;
	align-items: center;
	gap: var(--sgnl-space-8);
}

/* Source name and mount location share the row and truncate; arrow + action stay fixed. */
.inline-card__source,
.inline-card__mount {
	flex: 1 1 0;
	min-width: 0;
}

/* Strikethrough line shown when the source is marked for disconnection. */
.inline-card {
	position: relative;
}

.inline-card::after {
	content: "";
	position: absolute;
	top: 50%;
	left: 50%;
	transform: translate(-50%, -50%);
	width: 85%;
	height: var(--sgnl-border-width-2);
	background: var(--sgnl-alert-error-background);
	opacity: 0;
	transition: opacity 0.2s;
	pointer-events: none;
}

.inline-card.marked-for-disconnect::after {
	opacity: 1;
}
</style>
